import { useState, useEffect } from "react";
import axios from "axios";
import { Loader } from "components/lib";
import styles from "./styles.module.scss";
import {
  ExternalLink,
  FileText,
  Clipboard,
  Users,
  User,
  Folder,
  Mail,
  Settings,
  HelpCircle,
  Info,
} from "feather-icons-react";

export function QuickLinksManager() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newLink, setNewLink] = useState({ title: "", url: "", icon: "external-link", order: 1 });
  const [error, setError] = useState("");
  const [isIconDropdownOpen, setIsIconDropdownOpen] = useState(false);

  // Icons available to select from
  const iconMap = {
    "external-link": ExternalLink,
    "file-text": FileText,
    clipboard: Clipboard,
    user: User,
    users: Users,
    folder: Folder,
    mail: Mail,
    settings: Settings,
    "help-circle": HelpCircle,
    info: Info,
  };

  const availableIcons = Object.keys(iconMap);

  const renderIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || iconMap["external-link"];
    return <IconComponent size={18} />;
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.iconSelector}`)) {
        setIsIconDropdownOpen(false);
      }
    };

    if (isIconDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isIconDropdownOpen]);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/quick-links");
      const sortedLinks = (response.data.data.data || []).sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      setLinks(sortedLinks);
    } catch (error) {
      console.error("Error fetching quick links:", error);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (link) => {
    try {
      setError("");
      if (!link.title || !link.url) {
        setError("Title and URL are required");
        return;
      }

      if (editing) {
        // Update existing link
        await axios.put(`/api/quick-links/${editing.id}`, link);
        setEditing(null);
      } else {
        // Create new link
        await axios.post("/api/quick-links", link);
      }
      
      // Refetch all links to get updated positions (including any swapped items)
      await fetchLinks();
      setNewLink({ title: "", url: "", icon: "external-link", order: links.length + 1 });
    } catch (error) {
      console.error("Error saving quick link:", error);
      setError("Failed to save link. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/quick-links/${id}`);
      setLinks(links.filter((link) => link.id !== id));
    } catch (error) {
      console.error("Error deleting quick link:", error);
    }
  };

  const handleEdit = (link) => {
    setEditing(link);
    setNewLink({ ...link });
  };

  const handleCancel = () => {
    setEditing(null);
    setNewLink({ title: "", url: "", icon: "external-link", order: links.length + 1 });
    setError("");
  };

  if (loading) return <Loader />;

  return (
    <div className={styles.quickLinksManager}>
      <div className={styles.linkForm}>
        <h3>{editing ? "Edit Link" : "Add New Link"}</h3>
        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.formGroup}>
          <label>Title</label>
          <input
            type="text"
            value={newLink.title}
            onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
            placeholder="Enter link title"
          />
        </div>

        <div className={styles.formGroup}>
          <label>URL</label>
          <input
            type="text"
            value={newLink.url}
            onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
            placeholder="Enter link URL"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Icon</label>
          <div className={styles.iconSelector}>
            <div
              className={styles.selectedIcon}
              onClick={() => setIsIconDropdownOpen(!isIconDropdownOpen)}
            >
              {renderIcon(newLink.icon)}
              <span>{newLink.icon}</span>
              <span className={styles.dropdownArrow}>▼</span>
            </div>
            {isIconDropdownOpen && (
              <div className={styles.iconDropdownMenu}>
                {availableIcons.map((icon) => (
                  <div
                    key={icon}
                    className={`${styles.iconOption} ${
                      newLink.icon === icon ? styles.selected : ""
                    }`}
                    onClick={() => {
                      setNewLink({ ...newLink, icon });
                      setIsIconDropdownOpen(false);
                    }}
                  >
                    {renderIcon(icon)}
                    <span>{icon}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Position</label>
          <input
            type="number"
            min="1"
            value={newLink.order || 1}
            onChange={(e) => setNewLink({ ...newLink, order: parseInt(e.target.value) || 1 })}
            placeholder="Position (1 = top)"
          />
        </div>

        <div className={styles.formActions}>
          <button className={styles.saveButton} onClick={() => handleSave(newLink)}>
            {editing ? "Update Link" : "Add Link"}
          </button>

          {editing && (
            <button className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className={styles.linksList}>
        <h3>Current Links</h3>
        {links.length === 0 ? (
          <p>No quick links have been added yet.</p>
        ) : (
          <table className={styles.linksTable}>
            <thead>
              <tr>
                <th>Position</th>
                <th>Title</th>
                <th>URL</th>
                <th>Icon</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id}>
                  <td>{link.order || "1"}</td>
                  <td>{link.title}</td>
                  <td>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.url.length > 30 ? link.url.substring(0, 30) + "..." : link.url}
                    </a>
                  </td>
                  <td>
                    <div className={styles.iconCell}>
                      {renderIcon(link.icon)}
                      <span>{link.icon}</span>
                    </div>
                  </td>
                  <td className={styles.actions}>
                    <button className={styles.editButton} onClick={() => handleEdit(link)}>
                      Edit
                    </button>
                    <button className={styles.deleteButton} onClick={() => handleDelete(link.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
